import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { BarChart3, FileText, Library, Users } from 'lucide-react';
import { Pill } from '@/components/ui/Badge';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { PageHeader } from '@/components/ui/PageHeader';
import { LoadingLine } from '@/components/ui/States';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import { roleLabel } from '@/features/admin-content/staff';
import { useCan, useCurrentUser, useIsStaff } from '@/features/admin-content/use-current-user';
import { StaffDashboard } from '@/features/staff-workspace/StaffDashboard';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/')({
  component: AdminHomePage,
});

function AdminHomePage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const staff = useIsStaff();
  const canContent = useCan('content.admin');
  const canAnalytics = useCan('analytics.read');
  const canPeople = useCan('users.admin');
  const canGenerate = useCan('content.generate');

  if (sessionPending || meQuery.isPending) {
    return <LoadingLine>Loading</LoadingLine>;
  }

  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }

  if (!staff) {
    return <StaffOnly />;
  }

  const staffRoles = (meQuery.data?.roles ?? []).filter((slug) => slug !== 'student');

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title="Overview"
        description="What needs attention in your skills, and your shortcuts."
        actions={
          staffRoles.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {staffRoles.map((slug) => (
                <Pill key={slug} tone="neutral">
                  {roleLabel(slug)}
                </Pill>
              ))}
            </div>
          ) : null
        }
      />

      {canContent ? <StaffDashboard /> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {canContent ? (
          <WorkspaceCard
            to="/admin/bank"
            icon={Library}
            title="Bank"
            description="Create and edit listening, reading, writing and speaking material. Old student work stays on the version they sat."
          />
        ) : null}
        {canContent ? (
          <WorkspaceCard
            to="/admin/content"
            icon={FileText}
            title="Content"
            description={
              canGenerate
                ? 'Start a generation run, then review and import AI passages.'
                : 'Review and import AI reading passages. You cannot start a new run.'
            }
          />
        ) : null}
        {canAnalytics ? (
          <WorkspaceCard
            to="/admin/analytics"
            icon={BarChart3}
            title="Analytics"
            description="See item use, marking, and spend."
          />
        ) : null}
        {canPeople ? (
          <WorkspaceCard
            to="/admin/users"
            icon={Users}
            title="People"
            description="Grant or remove Teacher and Admin roles."
          />
        ) : null}
      </div>
    </div>
  );
}

function WorkspaceCard({
  to,
  icon,
  title,
  description,
}: {
  to: '/admin/bank' | '/admin/content' | '/admin/analytics' | '/admin/users';
  icon: typeof FileText;
  title: string;
  description: string;
}) {
  return (
    <Link to={to} className="block no-underline">
      <Card padding="lg" interactive className="grid gap-2">
        <div className="flex items-center gap-2.5">
          <Icon icon={icon} size={20} />
          <h2 className="font-display text-h3">{title}</h2>
        </div>
        <p className="text-body text-ink-2">{description}</p>
        <span className={buttonClassName({ variant: 'ghost', size: 'sm' })}>Open</span>
      </Card>
    </Link>
  );
}
